/**
 * Сохранённые банковские карты (Paylov, R-100…R-102). Полный номер уходит
 * бэкенду один раз при привязке и дальше нигде не живёт: в ответах только
 * маска. Привязка — два шага: номер и срок, затем код, который прислал банк.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { components } from '@dorixona/api'
import { useApi } from '../../api/context'
import { keys } from '../../api/keys'

type CardDto = components['schemas']['Card']

export interface SavedCard {
  id: number
  /** Маска вида 860012******9999. */
  number: string
  vendor: string
}

export function toCard(dto: CardDto): SavedCard {
  return { id: dto.id ?? 0, number: dto.number ?? '', vendor: dto.vendor ?? '' }
}

export function useCards(enabled: boolean) {
  const api = useApi()
  return useQuery({
    queryKey: keys.cards,
    queryFn: async () =>
      ((await api.get<{ items?: CardDto[] }>('/me/cards')).items ?? []).map(toCard),
    enabled,
  })
}

export interface CardChallenge {
  cardId: number
  /** Маска номера, на который банк отправил код. */
  phone: string
}

/** Шаг 1: номер и срок уходят в банк, банк шлёт код. */
export function useAddCard() {
  const api = useApi()
  return useMutation({
    mutationFn: async (input: { number: string; expire: string }) => {
      const done = await api.post<{ card_id?: number; phone?: string }>('/me/cards', input)
      return { cardId: done.card_id ?? 0, phone: done.phone ?? '' } satisfies CardChallenge
    },
  })
}

/** Шаг 2: код из СМС банка. Карта становится активной и попадает в список. */
export function useConfirmCard() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ cardId, code }: { cardId: number; code: string }) =>
      toCard(await api.post<CardDto>(`/me/cards/${cardId}/confirm`, { code })),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: keys.cards })
      // Сервер отдаёт saved_card доступным, только когда карта есть.
      await client.invalidateQueries({ queryKey: keys.checkoutOptions })
    },
  })
}

/** Удалить карту (R-102). Она пропадает из списка и из способов оплаты. */
export function useDeleteCard() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (cardId: number) => {
      await api.del<unknown>(`/me/cards/${cardId}`)
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: keys.cards })
      // Последнюю карту удалили — сервер снова скажет `no_saved_card`.
      await client.invalidateQueries({ queryKey: keys.checkoutOptions })
    },
  })
}
